import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Icon, PageTitle, useIntl } from "@metsooutotec/modes-react-components"
import { useNavigate } from "react-router-dom"
import stockManagementStore from "features/iot/stock-management/stores/stock-management-store"
import { groupSensorBatchesStockManagement } from "./stock-management-helper"
import { style } from "./stock-managment-styles"
import { StockManagement } from "features/iot/stock-management/models/stock-management-models"
import ListView, { TemplateItem } from "components/utils/list-view/list-view"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import LowSpinner from "components/common/low-spinner"

const StockManagementListView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const [dataTable, setDataTable] = useState<StockManagement[]>([])
    const [dataLoading, setDataLoading] = useState<boolean>(true)

    const loadDataTable = async () => {
        try {
            // Remove Sensor Batches those having null in partNumber.
            if (stockManagementStore.allSensorsStockManagement.length < 1) {
                setDataLoading(true)
                return
            }
            const convertData = groupSensorBatchesStockManagement()
            setDataTable(convertData)
        } catch (e) {
            console.error("Something wrong while loading the sensors!", e)
        } finally {
            setDataLoading(false)
        }
    }

    useEffect(() => {
        loadDataTable()
    }, [stockManagementStore.allSensorsStockManagement])

    const editStockManegement = (event: React.MouseEvent, partNumber: string) => {
        event.stopPropagation()
        navigate(`/stock-management/stock-allocation/${partNumber}`)
    }
    const onClickRow = (partNumber: string) => {
        navigate(`/stock-management/onhand-sensors/${partNumber}`)
    }

    const tableTemplate: TemplateItem<StockManagement>[] = [
        {
            headingKey: "headings.partNumber",
            cellFunc: (s: StockManagement) => {
                return s.partNumber
            },
            isSearchable: true,
        },
        {
            headingKey: "headings.onHand",
            cellFunc: (s: StockManagement) => {
                return s.onHand
            },
        },
        {
            headingKey: "headings.onOrder",
            cellFunc: (s: StockManagement) => {
                return s.onOrder
            },
        },
        {
            headingKey: "headings.allocated",
            cellFunc: (s: StockManagement) => {
                return s.allocated
            },
        },
        {
            headingKey: "headings.available",
            cellFunc: (s: StockManagement) => {
                return s.available < 0 ? <span style={{ color: "red" }}>{s.available}</span> : s.available
            },
        },
        {
            headingKey: "headings.dispatched",
            cellFunc: (s: StockManagement) => {
                return s.dispatched
            },
        },
        {
            headingKey: "headings.ableToCommit",
            cellFunc: (s: StockManagement) => {
                return s.ableToCommit
            },
        },
        {
            headingKey: "headings.actions",
            cellFunc: (s: StockManagement) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon name='icon-edit-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => editStockManegement(event, s.partNumber)} />
                </span>
            ),
        },
    ]

    return (
        <>
            <style>{style}</style>
            <PageTitle onPathChange={navigate} title={formatMessage("stockManagement.title")} />
            <div style={itemViewDetailsContainerStyles}>
                {dataLoading ? (
                    <LowSpinner id={"stockManagement.loading"} />
                ) : (
                    <ListView
                        noneYetKey={"sensorBatch.nonYet"}
                        template={tableTemplate}
                        data={dataTable}
                        onClickRow={(s: StockManagement) => onClickRow(s.partNumber)}
                    />
                )}
            </div>
        </>
    )
})

export default StockManagementListView
